import { trpcClient } from "./trpc";
import type { User, AuthTokens } from "./axios";

// API Error interface
export interface ApiError {
  code: string;
  message: string;
}

// Auth API request/response types
export interface SignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  deviceFingerprint: string;
}

export interface SignUpResponse {
  success: boolean;
  data: {
    userSub: string;
    message: string;
  };
}

export interface ConfirmSignUpRequest {
  email: string;
  code: string;
}

export interface ResendVerificationCodeRequest {
  email: string;
}

export interface SignInRequest {
  email: string;
  password: string;
  deviceFingerprint: string;
}

export interface SignInResponse {
  success: boolean;
  data: AuthTokens & {
    user: User;
  };
}

// Refresh token response (refresh token itself is provided by the client)
export interface RefreshTokenResponse {
  success: boolean;
  data: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
}

export interface LogoutRequest {
  refreshToken: string;
  cognitoRefreshToken: string;
}

export type OAuthProvider = "GOOGLE" | "APPLE";

export interface OAuthSignInRequest {
  provider: OAuthProvider;
  redirectUri: string;
}

export interface OAuthSignInResponse {
  success: boolean;
  data: {
    authorizationUrl: string;
    state: string;
  };
}

export interface OAuthCallbackRequest {
  code: string;
  state: string;
  redirectUri: string;
}

export interface OAuthCallbackResponse {
  success: boolean;
  data: AuthTokens & {
    user: User;
  };
}

// Auth API functions (tRPC-first)
export const authApi = {
  signUp: async (data: SignUpRequest): Promise<SignUpResponse> => {
    const response = await trpcClient.auth.signUp.mutate(data);
    return response;
  },

  confirmSignUp: async (data: ConfirmSignUpRequest) => {
    const response = await trpcClient.auth.confirmSignUp.mutate(data);
    return response;
  },

  resendVerificationCode: async (data: ResendVerificationCodeRequest) => {
    const response = await trpcClient.auth.resendConfirmationCode.mutate(data);
    return response;
  },

  signIn: async (data: SignInRequest): Promise<SignInResponse> => {
    const response = await trpcClient.auth.signIn.mutate(data);

    // Map backend Cognito user payload to our frontend User shape
    const backendUser = response.data.user as unknown as {
      sub: string;
      email: string;
      email_verified?: boolean;
      name?: string;
      given_name?: string;
      family_name?: string;
      username?: string;
      emailVerified?: boolean;
    };

    const mappedUser: User = {
      sub: backendUser.sub,
      email: backendUser.email,
      username:
        backendUser.username ||
        backendUser.name ||
        `${backendUser.given_name ?? ""} ${backendUser.family_name ?? ""}`.trim() ||
        backendUser.email ||
        "Unknown User",
      emailVerified: backendUser.emailVerified ?? backendUser.email_verified ?? false,
    };

    return {
      success: response.success,
      data: {
        accessToken: response.data.accessToken,
        idToken: response.data.idToken,
        refreshToken: response.data.refreshToken,
        cognitoRefreshToken: response.data.cognitoRefreshToken,
        expiresIn: response.data.expiresIn,
        user: mappedUser,
      },
    };
  },

  // Note: backend expects { refreshToken, cognitoRefreshToken, deviceFingerprint } payload
  refresh: async (
    refreshToken: string,
    cognitoRefreshToken: string,
    deviceFingerprint: string,
  ): Promise<RefreshTokenResponse> => {
    const response = await trpcClient.auth.refresh.mutate({
      refreshToken,
      cognitoRefreshToken,
      deviceFingerprint,
    });
    return response;
  },

  forgotPassword: async (data: ForgotPasswordRequest) => {
    const response = await trpcClient.auth.forgotPassword.mutate(data);
    return response;
  },

  resetPassword: async (data: ResetPasswordRequest) => {
    const response = await trpcClient.auth.resetPassword.mutate(data);
    return response;
  },

  logout: async (data: LogoutRequest) => {
    const response = await trpcClient.auth.logout.mutate(data);
    return response;
  },

  me: async (): Promise<{ success: boolean; data: User }> => {
    const response = await trpcClient.auth.me.query();

    const mapped: User = {
      sub: response.data.sub,
      email: response.data.email,
      username: response.data.name || response.data.email, // Use name or email as username
      emailVerified: response.data.isVerified ?? false,
    };

    return { success: response.success, data: mapped };
  },

  // OAuth methods
  initiateOAuthSignIn: async (data: OAuthSignInRequest): Promise<OAuthSignInResponse> => {
    const response = await trpcClient.auth.initiateOAuthSignIn.mutate(data);
    return response;
  },

  handleOAuthCallback: async (data: OAuthCallbackRequest): Promise<OAuthCallbackResponse> => {
    const response = await trpcClient.auth.oauthCallback.mutate(data);

    // Map backend user payload to frontend User shape
    const backendUser = response.data.user as unknown as {
      sub: string;
      email: string;
      email_verified?: boolean;
      name?: string;
      given_name?: string;
      family_name?: string;
      username?: string;
      emailVerified?: boolean;
    };

    const mappedUser: User = {
      sub: backendUser.sub,
      email: backendUser.email,
      username:
        backendUser.username ||
        backendUser.name ||
        `${backendUser.given_name ?? ""} ${backendUser.family_name ?? ""}`.trim() ||
        backendUser.email ||
        "Unknown User",
      emailVerified: backendUser.emailVerified ?? backendUser.email_verified ?? false,
    };

    return {
      success: response.success,
      data: {
        accessToken: response.data.accessToken,
        idToken: response.data.idToken,
        refreshToken: response.data.refreshToken,
        cognitoRefreshToken: response.data.cognitoRefreshToken,
        expiresIn: response.data.expiresIn,
        user: mappedUser,
      },
    };
  },
};

// Error code to message mapping
export const AUTH_ERROR_MESSAGES = {
  AUTH001: "Invalid email or password",
  AUTH002: "Email already registered",
  AUTH003: "Token expired",
  AUTH004: "Token invalid",
  AUTH005: "Rate limit exceeded",
  AUTH006: "Password reset token invalid",
  AUTH007: "User not confirmed",
  AUTH008: "User not found",
  AUTH009: "Invalid verification code",
  AUTH010: "Verification code expired",
  AUTH021: "Password does not meet requirements",
} as const;

export const getAuthErrorMessage = (code: string): string => {
  return (
    AUTH_ERROR_MESSAGES[code as keyof typeof AUTH_ERROR_MESSAGES] || "An unexpected error occurred"
  );
};
